"use client";

import React, { useMemo, useRef, useState } from "react";
import Script from "next/script";
import { CalendlyWidget, Input, Select } from "@/components/ui";
import { env } from "@/lib/environment";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import type { TrackingId } from "@/lib/tracking";
import { trackDemoLead } from "@/lib/tracking/ga";

const COMPANY_SIZE_OPTIONS = [
  { value: "1-50", label: "1–50" },
  { value: "51-250", label: "51–250" },
  { value: "251-1000", label: "251–1,000" },
  { value: "1000+", label: "1,000+" },
] as const;

const DEFAULT_PRIMARY_PROBLEM_OPTIONS = [
  { value: "safety-briefings", label: "Safety briefings" },
  { value: "shift-communication", label: "Shift communication" },
  { value: "compliance-tracking", label: "Compliance tracking" },
  { value: "hr-policy-updates", label: "HR policy updates" },
] as const;

const fieldClassName = "rounded-lg border-[#D7D9DE] bg-white";

interface DemoRequestInlineFormProps {
  solutionContext: string;
  primaryProblemOptions?: ReadonlyArray<{ value: string; label: string }>;
  buttonTrackingId?: TrackingId;
  reassuranceText?: string;
  buttonText?: string;
  /** Value of the form's data-lead attribute (analytics/lead hook). */
  dataLead?: string;
}

export default function DemoRequestInlineForm({
  solutionContext,
  primaryProblemOptions = DEFAULT_PRIMARY_PROBLEM_OPTIONS,
  buttonTrackingId = "cta-book-a-demo",
  reassuranceText = "No commitment required. 20-minute session with a specialist.",
  buttonText = "Request a Demo",
  dataLead = "demo-request",
}: DemoRequestInlineFormProps) {
  const { geoData } = useGeoLocation();
  const { getAllTrackingData } = useUTMContext();
  const hasTrackedLeadRef = useRef(false);
  const [formData, setFormData] = useState({
    email: "",
    company: "",
    company_size: "",
    primary_problem: "",
  });

  const companySizeLabel = useMemo(
    () => COMPANY_SIZE_OPTIONS.find(option => option.value === formData.company_size)?.label ?? "",
    [formData.company_size]
  );

  const primaryProblemLabel = useMemo(
    () =>
      primaryProblemOptions.find(option => option.value === formData.primary_problem)?.label ?? "",
    [formData.primary_problem, primaryProblemOptions]
  );

  const isComplete =
    formData.email.trim().length > 0 &&
    formData.company.trim().length > 0 &&
    formData.company_size.trim().length > 0 &&
    formData.primary_problem.trim().length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitDataOnZoho = async () => {
    try {
      const grecaptcha = (window as Window & { grecaptcha?: { execute: (key: string, opts: { action: string }) => Promise<string> } }).grecaptcha;
      if (!grecaptcha?.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, {
        action: "submit",
      });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      const details = {
        name: formData.company,
        email: formData.email,
        phone: "-",
        token,
        leads_source: "Calendly",
        company_name: formData.company,
        description: [
          `Solution context: ${solutionContext}`,
          `Company size: ${companySizeLabel}`,
          `Primary problem: ${primaryProblemLabel}`,
        ].join(" | "),
        form_name: "demo_request_inline",
        submitted_at: new Date().toISOString(),
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
        },
        ...getAllTrackingData(),
      };

      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ details }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        throw new Error(result.message || "Submission failed");
      }

      if (!hasTrackedLeadRef.current) {
        hasTrackedLeadRef.current = true;
        trackDemoLead();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-default">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${env.NEXT_PUBLIC_GRECAPTCHA_KEY}`}
        strategy="afterInteractive"
      />

      <form
        data-lead={dataLead}
        onSubmit={event => event.preventDefault()}
        className="grid grid-cols-1 gap-5 p-6 sm:grid-cols-2 md:p-8"
      >
        <Input
          id="demo-email"
          name="email"
          type="email"
          label="Work email*"
          placeholder="you@company.com"
          size="md"
          value={formData.email}
          onChange={handleChange}
          inputClassName={fieldClassName}
          required
          autoComplete="email"
        />
        <Input
          id="demo-company"
          name="company"
          type="text"
          label="Company*"
          placeholder="Your company name"
          size="md"
          value={formData.company}
          onChange={handleChange}
          inputClassName={fieldClassName}
          required
          autoComplete="organization"
        />
        <Select
          id="demo-company-size"
          name="company_size"
          label="Company size*"
          size="md"
          value={formData.company_size}
          onChange={handleChange}
          selectClassName={fieldClassName}
          required
        >
          <option value="" disabled>
            Select company size
          </option>
          {COMPANY_SIZE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Select
          id="demo-primary-problem"
          name="primary_problem"
          label="Primary problem*"
          size="md"
          value={formData.primary_problem}
          onChange={handleChange}
          selectClassName={fieldClassName}
          required
        >
          <option value="" disabled>
            What do you need help with?
          </option>
          {primaryProblemOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <div className="space-y-3 pt-1 sm:col-span-2">
          <CalendlyWidget
            buttonText={buttonText}
            buttonVariant="primary"
            buttonSize="lg"
            buttonClassName="w-full"
            buttonTrackingId={buttonTrackingId}
            disabled={!isComplete}
            preload
            autoPopulate={{
              email: formData.email,
              company_name: formData.company,
              help_with: primaryProblemLabel || null,
            }}
            onButtonClick={handleSubmitDataOnZoho}
          />
          {reassuranceText ? (
            <p className="text-center text-xs text-text-secondary">{reassuranceText}</p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
